import React from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type { ChatMessage as ChatMessageType } from "../../types/frontend";
import AttachmentChip from "./AttachmentChip";
import { styles as chatPanelStyles } from "./styles";

import { MessageRoles } from "../../types/frontend";
import { useChatContext } from "../../contexts/ChatContext";
import { ChatProgress } from "./ChatProgress";
import { theme as customTheme } from "../../theme";

/**
 * Props for the ChatMessage component.
 */
interface ChatMessageProps {
  chat: ChatMessageType; // The chat message data
  styles: any; // Styles to be applied
  isLastMessage: boolean; // Indicates if this is the last message in the chat
  isLoading?: boolean; // Optional flag to indicate loading state
}

/**
 * Custom link renderer for markdown links.
 * Opens links in a new tab with specific styling.
 */
function LinkRenderer(props: React.ComponentPropsWithRef<"a">) {
  return (
    <a
      {...props}
      target="_blank"
      rel="noreferrer"
      style={{
        color: customTheme.palette.text.primary,
        textDecoration: "underline",
      }}
    >
      {props.children}
    </a>
  );
}

/**
 * ChatMessage Component
 *
 * Renders a chat message with support for markdown content, progress display,
 * and attachment chips for sources.
 *
 * @param {ChatMessageProps} props - Component props
 * @returns {JSX.Element} Rendered ChatMessage component
 */
const ChatMessage: React.FC<ChatMessageProps> = ({
  chat,
  styles,
  isLastMessage,
  isLoading,
}) => {
  const theme = useTheme(); // Access the current theme for styling

  const { progressUpdate } = useChatContext(); // Get progress update from chat context

  // Determine if the message is from the assistant
  const isAssistant = chat.role === MessageRoles.Assistant;

  // Determine if progress should be shown
  const shouldShowProgress =
    isAssistant &&
    chat.isProgress &&
    progressUpdate &&
    isLastMessage &&
    !chat.content;

  /**
   * Renders the sources as attachment chips if available.
   * @returns {JSX.Element | null} Rendered sources or null if no sources
   */
  const renderSources = () => {
    if (!chat.sources || chat.sources.length === 0) {
      return null;
    }

    return (
      <Box sx={chatPanelStyles.attachmentChipMessageContainer(theme)}>
        {chat.sources.map((source) => (
          <AttachmentChip
            key={source.id}
            source={source}
            clickable={true}
            onClick={(url) => window.open(url, "_blank", "noopener,noreferrer")}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      {isAssistant ? (
        // Render assistant message with optional progress or markdown content
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              ...styles.messageContent(chat.role, theme),
            }}
          >
            {shouldShowProgress ? (
              <Box sx={{ color: theme.palette.text.secondary }}>
                <ChatProgress progress={progressUpdate} />
              </Box>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{ a: LinkRenderer }}
              >
                {chat.content}
              </ReactMarkdown>
            )}
          </Box>
        </Box>
      ) : (
        // Render user message with markdown content and sources
        <Box sx={styles.messageContent(chat.role, theme)}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {chat.content}
          </ReactMarkdown>
          {renderSources()}
        </Box>
      )}
    </>
  );
};

export default ChatMessage;
